import dayjs from 'dayjs';
import type { CalendarItem } from '../../hooks/useCalendarApi';

export interface DayCell {
  date: string;
  isInMonth: boolean;
  items: CalendarItem[];
  /** Сколько пунктов не поместилось в ячейку. */
  more: number;
}

export interface PeriodBar {
  item: CalendarItem;
  week: number;
  startCol: number;
  span: number;
  /** Строка внутри недели: пересекающиеся периоды идут друг под другом. */
  lane: number;
}

/** Календарная дата `YYYY-MM-DD` момента в поясе организации. */
export const zonedDay = (iso: string, timeZone: string): string =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(iso));

/** Сетка 6×7 от понедельника: месяц всегда занимает одинаковое место. */
export function monthWeeks(year: number, month: number): string[][] {
  const first = dayjs(`${year}-${String(month).padStart(2, '0')}-01`);
  const shift = (first.day() + 6) % 7; // воскресенье = 0 → понедельник первый
  let cursor = first.subtract(shift, 'day');
  const weeks: string[][] = [];
  for (let w = 0; w < 6; w++) {
    const week: string[] = [];
    for (let d = 0; d < 7; d++) {
      week.push(cursor.format('YYYY-MM-DD'));
      cursor = cursor.add(1, 'day');
    }
    weeks.push(week);
  }
  return weeks;
}

export function layoutMonth(
  year: number,
  month: number,
  items: CalendarItem[],
  timeZone: string,
  perDay = 3,
): { weeks: DayCell[][]; periods: PeriodBar[] } {
  const grid = monthWeeks(year, month);
  const prefix = `${year}-${String(month).padStart(2, '0')}`;
  const byDay = new Map<string, CalendarItem[]>();
  const periods: PeriodBar[] = [];

  for (const item of items) {
    if (item.type === 'period' && item.end) {
      const first = zonedDay(item.start, timeZone);
      // Граница периода исключающая: последний его день — накануне.
      const last = dayjs(zonedDay(item.end, timeZone)).subtract(1, 'day').format('YYYY-MM-DD');
      grid.forEach((week, w) => {
        const columns = week
          .map((date, i) => (date >= first && date <= last ? i : -1))
          .filter((i) => i >= 0);
        if (columns.length) {
          periods.push({ item, week: w, startCol: columns[0], span: columns.length, lane: 0 });
        }
      });
      continue;
    }
    const day = zonedDay(item.start, timeZone);
    byDay.set(day, [...(byDay.get(day) ?? []), item]);
  }

  // Дорожки: период встаёт в первую строку недели, где он ни с кем не пересекается.
  for (let w = 0; w < grid.length; w++) {
    const laneEnds: number[] = []; // последний занятый столбец каждой дорожки
    const inWeek = periods.filter((p) => p.week === w).sort((a, b) => a.startCol - b.startCol);
    for (const bar of inWeek) {
      const free = laneEnds.findIndex((end) => end < bar.startCol);
      bar.lane = free === -1 ? laneEnds.length : free;
      laneEnds[bar.lane] = bar.startCol + bar.span - 1;
    }
  }

  const weeks: DayCell[][] = grid.map((week) =>
    week.map((date) => {
      const all = byDay.get(date) ?? [];
      return {
        date,
        isInMonth: date.startsWith(prefix),
        items: all.slice(0, perDay),
        more: Math.max(0, all.length - perDay),
      };
    }),
  );
  return { weeks, periods };
}
